import axios from "axios";
import { gritifyApiUrl } from "./ServiceHelper";

/**
 * Skill with importance score for the recruitment
 */
export interface SkillWithImportance {
  /** Canonical skill ID from register */
  id: string;
  /** Importance score between 0.0 and 1.0 where 1.0 is must-have */
  importance: number;
}

/**
 * Task with importance score for the recruitment
 */
export interface TaskWithImportance {
  /** Canonical task ID from register */
  id: string;
  /** Importance score between 0.0 and 1.0 where 1.0 is must-have */
  importance: number;
}

/**
 * Result from parsing a job posting text using AI
 */
/**
 * Follow-up question from AI to clarify requirements
 */
export interface FollowUpQuestion {
  /** Unique identifier for the question */
  id: string;
  /** The question text */
  question: string;
  /** Type of answer expected */
  answerType: "text" | "singleChoice" | "multipleChoice";
  /** Options for choice-based questions */
  options?: string[];
}

export interface JobTextParserResult {
  /** Job title extracted from the posting */
  jobTitle: string | null;
  /** Minimum years of experience required */
  yearsOfExperienceMin: number | null;
  /** Maximum years of experience required */
  yearsOfExperienceMax: number | null;
  /** Languages required or preferred for the job */
  languages: string[];
  /** Skills with importance score (0.0-1.0) where 1.0 is must-have */
  skills: SkillWithImportance[];
  /** Tasks with importance score (0.0-1.0) where 1.0 is must-have */
  tasks: TaskWithImportance[];
  /** Required education level (canonical value from EducationDegrees) */
  musthaveEducationLevel: string | null;
  /** Wanted education domains/fields (canonical IDs) */
  educationWanted: string[];
  /** Maximum monthly salary excluding bonus (SEK) */
  maxSalaryExclBonus: number | null;
  /** Maximum bonus amount (SEK) */
  maxBonus: number | null;
  /** Work from home policy (canonical value: Remote, Hybrid, Onsite) */
  workFromHome: string | null;
  /** Benefits and perks extracted as free text */
  perks: string[];
  /** Other requirements that don't fit into other categories (free text) */
  otherRequirements: string[];
  /** Attractive pitch text to send to talents (max 3 sentences) */
  talentPitchText: string | null;
  /** Best matching role IDs from the register (up to 3) */
  bestRoleMatch: string[];
  /** Role candidates with scores when no confident best match is found */
  roleCandidates?: {
    id: string;
    nameSv: string;
    nameEn: string;
    score: number;
  }[];
  /** Matched office ID from the company's offices */
  officeId?: string | null;
  /** Announcement title for the job ad shown to candidates */
  announcementTitle?: string | null;
  /** Ingress text shown to candidates */
  ingress?: string | null;
  /** Follow-up questions to clarify requirements */
  followUpQuestions?: FollowUpQuestion[];
  /** Whether the job involves people management / staff responsibility */
  hasPeopleManagement?: boolean | null;
}

/**
 * Parse job text or job URL using AI to extract structured data.
 * Returns an operationId immediately. The actual result is delivered
 * via SignalR message type "ParseJobTextResult" with the same operationId.
 *
 * @param v.jobText - Raw job posting text to parse (optional if jobUrl or teamTailorJobId is provided)
 * @param v.jobUrl - URL to a job ad to scrape and parse (optional if jobText or teamTailorJobId is provided)
 * @param v.teamTailorJobId - TeamTailor job ID to fetch and parse (optional if jobText or jobUrl is provided)
 */
export const parseJobText = (v: {
  accessToken: string;
  jobText?: string;
  jobUrl?: string;
  teamTailorJobId?: string;
}): Promise<{ operationId: string }> => {
  return axios
    .post<{ operationId: string }>(
      `${gritifyApiUrl}/ParseJobTextAsync`,
      {
        jobText: v.jobText,
        jobUrl: v.jobUrl,
        teamTailorJobId: v.teamTailorJobId,
      },
      {
        headers: {
          Authorization: `Bearer ${v.accessToken}`,
        },
      }
    )
    .then(r => r.data);
};

export const getTranslatedTextFromAI = (v: {
  accessToken: string;
  text: string;
  fromLang: string;
  toLang: string;
}): Promise<string | null> => {
  return axios
    .post<string | null>(
      `${gritifyApiUrl}/GetTranslatedTextFromAI`,
      {
        text: v.text,
        fromLang: v.fromLang,
        toLang: v.toLang,
      },
      {
        headers: {
          Authorization: `Bearer ${v.accessToken}`,
        },
      }
    )
    .then(r => r.data);
};

export default class AiDataService {
  public static getVisualIngressAiHelp(v: {
    accessToken: string;
    recruitmentId: string;
    lang: string;
  }): Promise<string> {
    return axios
      .get<string>(
        `${gritifyApiUrl}/GetVisualIngressAiHelp?recruitmentId=${v.recruitmentId}&lang=${v.lang}`,
        {
          headers: {
            authorization: `Bearer ${v.accessToken}`,
          },
        }
      )
      .then(x => x.data);
  }

  public static getVisualHeadingAiHelp(v: {
    accessToken: string;
    recruitmentId: string;
    lang: string;
  }): Promise<string> {
    return axios
      .get<string>(
        `${gritifyApiUrl}/GetVisualHeadingAiHelp?recruitmentId=${v.recruitmentId}&lang=${v.lang}`,
        {
          headers: {
            authorization: `Bearer ${v.accessToken}`,
          },
        }
      )
      .then(x => x.data);
  }

  public static getVisualHeadingAiFromText(v: {
    accessToken: string;
    recruitmentId: string;
    lang: string;
    text: string;
  }): Promise<string> {
    return axios
      .post<string>(
        `${gritifyApiUrl}/GetVisualHeadingAiFromText?recruitmentId=${v.recruitmentId}&lang=${v.lang}`,
        {
          recruitmentId: v.recruitmentId,
          lang: v.lang,
          Text: v.text,
        },
        {
          headers: {
            authorization: `Bearer ${v.accessToken}`,
          },
        }
      )
      .then(x => x.data);
  }

  public static getVisualTasksAiHelp(v: {
    accessToken: string;
    recruitmentId: string;
  }): Promise<string[]> {
    return axios
      .get<string[]>(
        `${gritifyApiUrl}/GetVisualTasksAiHelp?recruitmentId=${v.recruitmentId}`,
        {
          headers: {
            authorization: `Bearer ${v.accessToken}`,
          },
        }
      )
      .then(x => x.data);
  }

  public static getChatMessageAIHelp(v: {
    accessToken: string;
    recruitmentId: string | null;
    talentId: string;
    userName: string;
  }): Promise<string> {
    return axios
      .post<string>(
        `${gritifyApiUrl}/GetChatMessageAIHelp`,
        {
          recruitmentId: v.recruitmentId,
          talentId: v.talentId,
          userName: v.userName,
        },
        {
          headers: {
            authorization: `Bearer ${v.accessToken}`,
          },
        }
      )
      .then(x => x.data);
  }
}
